import axios from 'axios';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/environment';
import { getCurrentOrgId } from '../utils/storage';

// =============================================================================
// ENVIRONMENT CONFIGURATION
// =============================================================================

// Promise that resolves when Firebase auth state is ready
let authReadyResolve;
let authReady = new Promise((resolve) => {
  authReadyResolve = resolve;
});
let authInitialized = false;

// Listen for Firebase auth state changes
onAuthStateChanged(auth, (user) => {
  console.log('[API] Firebase auth state changed:', user ? user.email : 'signed out');
  if (!authInitialized) {
    authInitialized = true;
    authReadyResolve();
  }
});

/**
 * Wait for Firebase auth to be ready, with timeout
 * Returns immediately if already initialized
 */
async function waitForAuth(timeoutMs = 5000) {
  if (authInitialized) return;

  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('Firebase auth timeout')), timeoutMs)
  );

  try {
    await Promise.race([authReady, timeout]);
  } catch {
    console.warn('[API] Firebase auth timeout, proceeding without Firebase token');
  }
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies for CSRF token
});

// CSRF token cache
let csrfToken = null;

// Fetch CSRF token
async function fetchCsrfToken() {
  try {
    console.log('[API] Fetching CSRF token from /csrf-token...');
    const response = await api.get('/csrf-token');
    csrfToken = response.data.csrfToken;
    console.log('[API] CSRF token fetched successfully:', csrfToken);
    return csrfToken;
  } catch (error) {
    console.error('[API] Failed to fetch CSRF token:', error);
    console.error('[API] Error details:', error.response?.data);
    return null;
  }
}

// Request interceptor - add a fresh Firebase ID token, org ID, and CSRF token.
api.interceptors.request.use(
  async (config) => {
    const orgId = getCurrentOrgId();

    // Wait for Firebase auth to be ready (handles page reload scenario)
    await waitForAuth();

    // Get fresh Firebase ID token if user is authenticated
    // This is the same pattern as the PWA - Firebase tokens are what the backend expects
    const firebaseUser = auth.currentUser;
    let token = null;
    if (firebaseUser) {
      try {
        token = await firebaseUser.getIdToken();
      } catch (error) {
        console.error('[API] Failed to get Firebase ID token:', error);
      }
    }

    console.log('[API Request]', {
      method: config.method?.toUpperCase(),
      url: config.url,
      hasToken: !!token,
      hasFirebaseUser: !!firebaseUser,
      authInitialized,
      orgId,
      data: config.data,
    });

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (orgId) {
      config.headers['X-Organization-Id'] = orgId;
    }

    // Add CSRF token for state-changing operations
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(config.method?.toUpperCase())) {
      // Fetch CSRF token if we don't have one
      if (!csrfToken) {
        await fetchCsrfToken();
      }
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401, 403 (CSRF), and token refresh
api.interceptors.response.use(
  (response) => {
    console.log('[API Response]', {
      status: response.status,
      url: response.config.url,
      data: response.data,
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.error('[API Response Error]', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.response?.data?.message || error.message,
      data: error.response?.data,
    });

    // If 403 with CSRF error, refresh CSRF token and retry
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === 'CSRF_TOKEN_INVALID' &&
      !originalRequest._csrfRetry
    ) {
      originalRequest._csrfRetry = true;
      console.log('[API] CSRF token invalid, fetching new token...');

      // Clear cached token and fetch new one
      csrfToken = null;
      await fetchCsrfToken();

      if (csrfToken) {
        originalRequest.headers['X-CSRF-Token'] = csrfToken;
        return api(originalRequest);
      }
    }

    // Firebase owns token refresh. Force one refresh and retry once on a 401.
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      if (auth.currentUser) {
        const token = await auth.currentUser.getIdToken(true);
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

// Convenience methods for different API sections
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  signup: (data) => api.post('/auth/signup/organization', data),
  // Organization signup with full details (founder flow)
  signupOrganization: (data) => api.post('/auth/signup/organization', data),
  logout: () => api.post('/auth/logout'),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  resetPassword: (email) => api.post('/auth/reset-password', { email }),
  googleAuth: (idToken) => api.post('/auth/google', { idToken }),
};

// Public organization API (no auth required)
export const publicAPI = {
  // Check if organization slug is available
  checkSlug: (slug) => api.get(`/public/check-slug/${slug}`),
};

export const testAPI = {
  // SendGrid tests
  sendEmail: (data) => api.post('/test/sendgrid/send', data),
  sendTemplateEmail: (data) => api.post('/test/sendgrid/send-template', data),
  verifyEmail: (email) => api.get(`/test/sendgrid/verify/${email}`),

  // Twilio tests
  sendSMS: (data) => api.post('/test/twilio/send-sms', data),
  sendWhatsApp: (data) => api.post('/test/twilio/send-whatsapp', data),
  verifyPhone: (phoneNumber) => api.get(`/test/twilio/verify/${phoneNumber}`),

  // Firebase Push tests
  sendPush: (data) => api.post('/test/firebase/send-push', data),
  sendMulticast: (data) => api.post('/test/firebase/send-multicast', data),
  sendTopic: (data) => api.post('/test/firebase/send-topic', data),

  // Cron job tests
  listCronJobs: () => api.get('/test/cron/list'),
  triggerJob: (jobName) => api.post(`/test/cron/trigger/${jobName}`),
  jobHistory: (jobName) => api.get(`/test/cron/history/${jobName}`),

  // Public endpoint tests
  healthCheck: () => axios.get(`${API_BASE_URL.replace(/\/api\/?$/, '')}/health`),
  publicRSVP: (token, response) => api.post(`/public/rsvp/${token}`, { response }),
  publicPrayer: (groupToken, data) => api.post('/public/prayer', { groupToken, ...data }),
};

export const orgAPI = {
  list: () => api.get('/users/me/organizations'),
  create: (data) => api.post('/organizations', data),
  get: (id) => api.get(`/organizations/${id}`),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  delete: (id) => api.delete(`/organizations/${id}`),
};

export const campusAPI = {
  list: () => api.get(`/campuses`),
  create: (orgId, data) => api.post(`/campuses`, data),
  get: (id) => api.get(`/campuses/${id}`),
  update: (id, data) => api.put(`/campuses/${id}`, data),
  delete: (id) => api.delete(`/campuses/${id}`),
};

export const ministryAPI = {
  list: () => api.get(`/ministries`),
  create: (campusId, data) => api.post(`/ministries`, { ...data, campusId }),
  get: (id) => api.get(`/ministries/${id}`),
  update: (id, data) => api.put(`/ministries/${id}`, data),
  delete: (id) => api.delete(`/ministries/${id}`),
};

export const groupAPI = {
  list: (ministryId) => api.get(`/groups`, { params: { ministryId } }),
  create: (ministryId, data) => api.post(`/groups`, { ...data, ministryId }),
  get: (id) => api.get(`/groups/${id}`),
  update: (id, data) => api.put(`/groups/${id}`, data),
  delete: (id) => api.delete(`/groups/${id}`),
  members: (id) => api.get(`/groups/${id}/members`),
  addMember: (id, data) => api.post(`/groups/${id}/members`, data),
  generateJoinCode: (id, data) => api.post(`/groups/${id}/generate-join-code`, data),
  generateJoinQR: (id, data) => api.post(`/groups/${id}/generate-join-qr`, data),
};

export const eventAPI = {
  create: (data) => api.post('/events', data),
  list: (groupId, params) => api.get(`/groups/${groupId}/events`, { params }),
  get: (eventId) => api.get(`/events/${eventId}`),
  update: (eventId, data) => api.put(`/events/${eventId}`, data),
  delete: (eventId) => api.delete(`/events/${eventId}`),
  generateRSVPs: (eventId) => api.post(`/events/${eventId}/generate-rsvps`),
  getRSVPs: (eventId) => api.get(`/events/${eventId}/rsvps`),
};

export const seedAPI = {
  seedTestData: (data) => api.post('/seed/test-data', data),
  clearTestData: (orgId) => api.delete(`/seed/test-data/${orgId}`),

  // ============================================================================
  // Persona Group Seeding (Fold OAuth)
  // ============================================================================

  // List ministries for organization (for dropdown)
  listMinistriesForOrg: (organizationId) =>
    api.get(`/seed/admin/list-ministries/${organizationId}`),

  // List groups for organization (for dropdown/deletion)
  listGroupsForOrg: (organizationId, ministryId = null) =>
    api.get(`/seed/admin/list-groups/${organizationId}`, {
      params: ministryId ? { ministryId } : {}
    }),

  // Seed a persona group (thriving, growing, stable, struggling, crisis, recovering)
  seedPersonaGroup: (data) =>
    api.post('/seed/admin/seed-persona-group', data),

  // Delete a group and all associated data
  deleteGroupCompletely: (groupId, deleteOrphanedUsers = true) =>
    api.delete(`/seed/admin/delete-group/${groupId}`, {
      params: { deleteOrphanedUsers }
    }),
};

export const adminAPI = {
  getCronStatus: () => api.get('/admin/cron/status'),
  triggerCronJob: (jobName, reason) => api.post(`/admin/cron/trigger/${jobName}`, { reason }),
  getStaleLocks: () => api.get('/admin/cron/locks'),
  releaseLock: (jobName, reason) => api.post(`/admin/cron/locks/release/${jobName}`, { reason }),
  // User management - Fold-authorized cross-tenant support tool
  lookupUser: (email) => api.get('/admin/users/lookup', {
    params: { email }
  }),
  deleteUserByEmail: (email) => api.delete('/admin/users/delete-by-email', {
    data: { email }
  }),
};

export const leakhunterAPI = {
  // Run a new analysis
  runAnalysis: (params) => api.post('/admin/leakhunter/analyze', params),

  // Get a specific analysis by ID
  getAnalysis: (id) => api.get(`/admin/leakhunter/analysis/${id}`),

  // Get historical analyses with optional filters
  getHistory: (params = {}) => api.get('/admin/leakhunter/history', { params }),

  // Test Railway API connectivity
  testConnection: () => api.get('/admin/leakhunter/test-connection'),

  // Get configuration and thresholds
  getConfig: () => api.get('/admin/leakhunter/config'),
};

// ============================================================================
// Cron Job Management API (Fold OAuth)
// ============================================================================

export const cronAPI = {
  // List all jobs with configuration and status
  listJobs: (params = {}) =>
    api.get('/admin/cron/jobs', { params }),

  // Get single job details with history
  getJob: (jobKey) =>
    api.get(`/admin/cron/jobs/${jobKey}`),

  // Update job configuration
  updateJob: (jobKey, updates) =>
    api.patch(`/admin/cron/jobs/${jobKey}`, updates),

  // Trigger a job manually
  triggerJob: (jobKey, reason) =>
    api.post(`/admin/cron/jobs/${jobKey}/trigger`, { reason }),

  // Get job execution history
  getJobHistory: (jobKey, params = {}) =>
    api.get(`/admin/cron/jobs/${jobKey}/history`, { params }),

  // Get dashboard summary
  getDashboard: () =>
    api.get('/admin/cron/dashboard'),

  // Get unified execution history across all jobs
  getUnifiedHistory: (params = {}) =>
    api.get('/admin/cron/history', { params }),

  // Legacy endpoints (for backwards compatibility)
  getLegacyStatus: () => api.get('/admin/cron/status'),
  getLegacyStaleLocks: () => api.get('/admin/cron/locks'),
  releaseLegacyLock: (jobName, reason) =>
    api.post(`/admin/cron/locks/release/${jobName}`, { reason }),
};

export const prayerAPI = {
  generateQR: (groupId, expirationDays = 7) =>
    api.post(`/prayers/groups/${groupId}/generate-prayer-qr`, { expirationDays }),
};

// ============================================================================
// Founder Dashboard Analytics API (Fold OAuth)
// ============================================================================

export const founderDashboardAPI = {
  // Health check
  health: () =>
    api.get('/admin/dashboard/health'),

  // Summary - Executive KPIs for hero cards
  getSummary: () =>
    api.get('/admin/dashboard/analytics/summary'),

  // Engagement - prayers, attendance, events, chat metrics
  getEngagement: () =>
    api.get('/admin/dashboard/analytics/engagement'),

  // Organizations list with metrics
  getOrganizations: (params = {}) =>
    api.get('/admin/dashboard/analytics/organizations', {
      params: { sort: params.sort, order: params.order, limit: params.limit, offset: params.offset },
    }),

  // Single organization detail
  getOrganization: (id) =>
    api.get(`/admin/dashboard/analytics/organizations/${id}`),

  // Single group detail
  getGroup: (id) =>
    api.get(`/admin/dashboard/analytics/groups/${id}`),

  // Single member detail
  getMember: (id) =>
    api.get(`/admin/dashboard/analytics/members/${id}`),

  // Growth - new orgs, members, groups trends
  getGrowth: () =>
    api.get('/admin/dashboard/analytics/growth'),

  // Retention - churn, member engagement tiers
  getRetention: () =>
    api.get('/admin/dashboard/analytics/retention'),

  // Revenue - MRR, subscriptions, tier breakdown
  getRevenue: () =>
    api.get('/admin/dashboard/analytics/revenue'),

  // Combined revenue overview - Stripe + Apple + internal, provider-split
  getRevenueOverview: () =>
    api.get('/admin/dashboard/analytics/revenue-overview'),

  // Infrastructure - database health, pool stats, cron jobs
  getInfrastructure: () =>
    api.get('/admin/dashboard/analytics/infrastructure'),

  // Feature Analytics API
  getFeatureSummary: (params = {}) =>
    api.get('/admin/dashboard/analytics/features/summary', {
      params
    }),

  getPrayerAnalytics: (params = {}) =>
    api.get('/admin/dashboard/analytics/features/prayer', {
      params
    }),

  getAttendanceAnalytics: (params = {}) =>
    api.get('/admin/dashboard/analytics/features/attendance', {
      params
    }),

  getEventsAnalytics: (params = {}) =>
    api.get('/admin/dashboard/analytics/features/events', {
      params
    }),

  getChatAnalytics: (params = {}) =>
    api.get('/admin/dashboard/analytics/features/chat', {
      params
    }),

  getOrgFeatureHealth: (orgId) =>
    api.get(`/admin/dashboard/analytics/features/org/${orgId}`, {
    }),

  getOrganizationsList: () =>
    api.get('/admin/dashboard/analytics/organizations', {
      params: { limit: 200, sort: 'name', order: 'asc' }
    }),

  getCampuses: (organizationId) =>
    api.get(`/admin/dashboard/analytics/organizations/${organizationId}/campuses`, {
    }),

  getGroupsByCampus: (campusId) =>
    api.get(`/admin/dashboard/analytics/campuses/${campusId}/groups`, {
    }),

  getAppUsageSummary: () =>
    api.get('/admin/dashboard/analytics/app-usage/summary', {
    }),

  getAppUsageTrends: (period = '30d') =>
    api.get('/admin/dashboard/analytics/app-usage/trends', {
      params: { period }
    }),

  getAppUsageActivity: () =>
    api.get('/admin/dashboard/analytics/app-usage/activity', {
    }),

  getAppUsageActivityTrends: (period = '30d') =>
    api.get('/admin/dashboard/analytics/app-usage/activity-trends', {
      params: { period }
    }),

  // Onboarding Analytics
  getOnboardingFunnels: ({ refresh = false, period = null } = {}) =>
    api.get('/admin/dashboard/analytics/onboarding/funnels', {
      params: { ...(refresh ? { refresh: true } : {}), ...(period ? { period } : {}) },
    }),

  getOnboardingTrends: () =>
    api.get('/admin/dashboard/analytics/onboarding/trends'),

  getOnboardingOrgs: () =>
    api.get('/admin/dashboard/analytics/onboarding/orgs'),

  getOnboardingUsers: () =>
    api.get('/admin/dashboard/analytics/onboarding/users'),

  getOnboardingUserCohorts: () =>
    api.get('/admin/dashboard/analytics/onboarding/users/cohorts'),

  getOnboardingTodaySnapshot: () =>
    api.get('/admin/dashboard/analytics/onboarding/users/today-snapshot'),

  getOnboardingStageUsers: (stage, cohort = null) =>
    api.get(`/admin/dashboard/analytics/onboarding/funnels/users/${stage}`, {
      params: cohort ? { cohort } : {},
    }),

  getOnboardingStepOrgs: (step) =>
    api.get(`/admin/dashboard/analytics/onboarding/funnels/orgs/${step}`),

  getOnboardingUserDetail: (userId) =>
    api.get(`/admin/dashboard/analytics/onboarding/users/${userId}`),

  getOnboardingOrgDetail: (orgId) =>
    api.get(`/admin/dashboard/analytics/onboarding/orgs/${orgId}`),

  getPersonalGroupFunnel: ({ refresh = false } = {}) =>
    api.get('/admin/dashboard/analytics/onboarding/personal-groups/funnel', {
      params: refresh ? { refresh: true } : {},
    }),

  getPersonalGroupCohorts: () =>
    api.get('/admin/dashboard/analytics/onboarding/personal-groups/cohorts'),

  getPersonalGroupStageUsers: (stage) =>
    api.get(`/admin/dashboard/analytics/onboarding/personal-groups/users/${stage}`),

  // Acquisition lifecycle Outreach (founder-only, masked and audited)
  getOutreachSummary: () =>
    api.get('/admin/dashboard/analytics/onboarding/outreach/summary'),

  updateOutreachProgram: (data) =>
    api.patch('/admin/dashboard/analytics/onboarding/outreach/program', data),

  getOutreachCampaigns: () =>
    api.get('/admin/dashboard/analytics/onboarding/outreach/campaigns'),

  updateOutreachCampaign: (campaignKey, data) =>
    api.patch(
      `/admin/dashboard/analytics/onboarding/outreach/campaigns/${encodeURIComponent(campaignKey)}`,
      data
    ),

  getOutreachCandidates: (params = {}) =>
    api.get('/admin/dashboard/analytics/onboarding/outreach/candidates', { params }),

  getOutreachDeliveries: (params = {}) =>
    api.get('/admin/dashboard/analytics/onboarding/outreach/deliveries', { params }),

  getOutreachSuppressions: (params = {}) =>
    api.get('/admin/dashboard/analytics/onboarding/outreach/suppressions', { params }),

  getOutreachTemplates: () =>
    api.get('/admin/dashboard/analytics/onboarding/outreach/templates'),

  getOutreachTemplatePreview: (templateKey, version, variant = 'with_name') =>
    api.get(
      `/admin/dashboard/analytics/onboarding/outreach/templates/${encodeURIComponent(
        templateKey
      )}/${encodeURIComponent(version)}/preview`,
      { params: { variant } }
    ),

  approveOutreachTemplate: (templateKey, version, data) =>
    api.post(
      `/admin/dashboard/analytics/onboarding/outreach/templates/${encodeURIComponent(
        templateKey
      )}/${encodeURIComponent(version)}/approve`,
      data
    ),

  testOutreachTemplate: (templateKey, version, data) =>
    api.post(
      `/admin/dashboard/analytics/onboarding/outreach/templates/${encodeURIComponent(
        templateKey
      )}/${encodeURIComponent(version)}/test-send`,
      data
    ),
};

// ============================================================================
// Church Demand Intelligence API (Admin-only, uses API Key auth)
// ============================================================================

export const churchDemandAPI = {
  // 1. GET /summary
  summary: () =>
    api.get('/admin/dashboard/analytics/church-demand/summary'),

  // 2. GET /clusters — paginated cluster list with filters
  clusters: (params = {}) =>
    api.get('/admin/dashboard/analytics/church-demand/clusters', {
      params,
    }),

  // 3. GET /clusters/:id — single cluster + sources + corrections
  cluster: (id) =>
    api.get(`/admin/dashboard/analytics/church-demand/clusters/${id}`, {
    }),

  // 4. PATCH /clusters/:id — apply correction (confirm | override | reject)
  correct: (id, body) =>
    api.patch(`/admin/dashboard/analytics/church-demand/clusters/${id}`, body, {
    }),

  // 5. POST /recluster — kick off background recluster job (202 / 409 / 503)
  recluster: () =>
    api.post('/admin/dashboard/analytics/church-demand/recluster', null, {
    }),

  // 6. GET /searches — raw FYC search stream (source_type=fyc_search server-side)
  searches: (params = {}) =>
    api.get('/admin/dashboard/analytics/church-demand/searches', {
      params,
    }),

  // 7. GET /trends — time-series aggregates (weeks param 1..26, default 12)
  trends: (params = {}) =>
    api.get('/admin/dashboard/analytics/church-demand/trends', {
      params,
    }),
};

export const externalServicesAPI = {
  // SendGrid Testing - Send invitation email
  // Uses the correct endpoint for group member invitations
  sendInvitation: (organizationId, data) =>
    api.post('/users/invite-group-member', data),

  // Twilio Testing - Trigger attendance prompt cron (sends SMS)
  triggerAttendancePrompt: (reason) =>
    api.post('/admin/cron/trigger/attendance-prompts', { reason }),

  // Firebase Push Testing - Create prayer (triggers push notification)
  createPrayer: (groupId, data) =>
    api.post('/prayers', { ...data, groupId }),

  // Get user's groups for prayer creation
  getUserGroups: (orgId) =>
    api.get('/groups', {
      headers: { 'X-Organization-ID': orgId }
    }),

  // Get prayers for a group (leader's prayer queue)
  getPrayers: (groupId, params = {}) =>
    api.get(`/prayers/groups/${groupId}/prayers`, { params }),
};

// ============================================================================
// Third-Party Analytics API (Admin-only, uses API Key auth)
// ============================================================================

// Helper to convert period string (7d, 30d, 90d) to days number
const periodToDays = (period) => {
  const match = period?.match(/^(\d+)d$/);
  return match ? parseInt(match[1], 10) : 30;
};

export const thirdPartyAnalyticsAPI = {
  // SendGrid
  getSendGridOverview: (period = '30d') =>
    api.get('/admin/dashboard/analytics/sendgrid/overview', { params: { days: periodToDays(period) } }),
  getSendGridByEmailType: (period = '30d') =>
    api.get('/admin/dashboard/analytics/sendgrid/by-email-type', { params: { days: periodToDays(period) } }),
  getSendGridByDomain: (period = '30d') =>
    api.get('/admin/dashboard/analytics/sendgrid/by-domain', { params: { days: periodToDays(period) } }),
  getSendGridTrends: (period = '30d') =>
    api.get('/admin/dashboard/analytics/sendgrid/trends', { params: { period } }),
  getSendGridSuppressions: () =>
    api.get('/admin/dashboard/analytics/sendgrid/suppressions'),

  // Stripe
  getStripeOverview: () =>
    api.get('/admin/dashboard/analytics/stripe/overview'),
  getStripeMrrByTier: () =>
    api.get('/admin/dashboard/analytics/stripe/mrr-by-tier'),
  getStripeFailedPayments: () =>
    api.get('/admin/dashboard/analytics/stripe/failed-payments'),
  getStripeChurnAnalysis: () =>
    api.get('/admin/dashboard/analytics/stripe/churn-analysis'),
  getStripeRevenueAtRisk: () =>
    api.get('/admin/dashboard/analytics/stripe/revenue-at-risk'),
  getStripeTierChanges: () =>
    api.get('/admin/dashboard/analytics/stripe/tier-changes'),

  // User Engagement (Firebase Auth)
  getUserEngagementOverview: () =>
    api.get('/admin/dashboard/analytics/firebase-auth/overview'),
  getUserEngagementTrends: (period = '30d') =>
    api.get('/admin/dashboard/analytics/firebase-auth/trends', { params: { period } }),
  getUserEngagementAuthMethods: () =>
    api.get('/admin/dashboard/analytics/firebase-auth/auth-methods'),
  getUserEngagementRetention: () =>
    api.get('/admin/dashboard/analytics/firebase-auth/retention'),
  getUserEngagementLoginHealth: () =>
    api.get('/admin/dashboard/analytics/firebase-auth/login-health'),

  // Firestore
  getFirestoreOverview: () =>
    api.get('/admin/dashboard/analytics/firestore/overview'),
  getFirestoreByCollection: () =>
    api.get('/admin/dashboard/analytics/firestore/by-collection'),
  getFirestoreCostProjection: () =>
    api.get('/admin/dashboard/analytics/firestore/cost-projection'),
  getFirestoreTrends: (days = 30) =>
    api.get(`/admin/dashboard/analytics/firestore/trends?days=${days}`),

  // Rekognition
  getRekognitionOverview: () =>
    api.get('/admin/dashboard/analytics/rekognition/overview'),
  getRekognitionByOperation: () =>
    api.get('/admin/dashboard/analytics/rekognition/by-operation'),
  getRekognitionByOrganization: () =>
    api.get('/admin/dashboard/analytics/rekognition/by-organization'),
  getRekognitionCoverage: () =>
    api.get('/admin/dashboard/analytics/rekognition/coverage'),
  getRekognitionProcessingTimes: () =>
    api.get('/admin/dashboard/analytics/rekognition/processing-times'),

  // R2 Storage
  getR2Overview: () =>
    api.get('/admin/dashboard/analytics/r2/overview'),
  getR2ByTier: () =>
    api.get('/admin/dashboard/analytics/r2/by-tier'),
  getR2ByOrganization: () =>
    api.get('/admin/dashboard/analytics/r2/by-organization'),
  getR2ByContentType: () =>
    api.get('/admin/dashboard/analytics/r2/by-content-type'),
  getR2Trends: (period = '30d') =>
    api.get('/admin/dashboard/analytics/r2/trends', { params: { period } }),

  // Compliance
  getComplianceOverview: () =>
    api.get('/admin/dashboard/analytics/compliance/overview'),
  getComplianceBipaByOrg: () =>
    api.get('/admin/dashboard/analytics/compliance/bipa-by-org'),
  getComplianceProfileAdoption: () =>
    api.get('/admin/dashboard/analytics/compliance/profile-adoption'),
  getComplianceCorrelation: () =>
    api.get('/admin/dashboard/analytics/compliance/correlation'),

  // Alerts
  getAlerts: (filters = {}) =>
    api.get('/admin/dashboard/analytics/alerts', { params: filters }),
  getAlertSummary: () =>
    api.get('/admin/dashboard/analytics/alerts/summary'),
  getAlertsByService: (service) =>
    api.get(`/admin/dashboard/analytics/alerts/by-service/${service}`),
  acknowledgeAlert: (id) =>
    api.post(`/admin/dashboard/analytics/alerts/${id}/acknowledge`, {}),

  // Service Health (aggregated status for Summary page)
  getServiceHealth: () =>
    api.get('/admin/dashboard/analytics/service-health'),
};

// ============================================================================
// Feedback API (Fold OAuth)
// ============================================================================

export const feedbackAPI = {
  // List all feedback with filters
  list: (params = {}) =>
    api.get('/admin/dashboard/feedback', { params }),

  // Get feedback statistics
  getStats: () =>
    api.get('/admin/dashboard/feedback/stats'),

  // Get single feedback
  get: (id) =>
    api.get(`/admin/dashboard/feedback/${id}`),

  // Get case thread (conversation + messages) for a feedback row
  getThread: (id) =>
    api.get(`/admin/dashboard/feedback/${id}/thread`),

  // Administrator identity is derived from the verified Fold session.
  reply: (id, message) =>
    api.post(`/admin/dashboard/feedback/${id}/reply`, { message }),

  // Update feedback (status, notes, github_issue_url)
  update: (id, data) =>
    api.patch(`/admin/dashboard/feedback/${id}`, data),
};

// ============================================================================
// User Management API (Fold OAuth)
// ============================================================================

export const userManagementAPI = {
  searchUsers: (query, limit = 25, offset = 0) =>
    api.get('/admin/user-management/search', { params: { q: query, limit, offset } }),
  getUser: (userId) =>
    api.get(`/admin/user-management/users/${userId}`),
  updateUser: (userId, data) =>
    api.patch(`/admin/user-management/users/${userId}`, data),
  getOrgTree: () =>
    api.get('/admin/user-management/org-tree'),
  getGroupMembers: (groupId) =>
    api.get(`/admin/user-management/groups/${groupId}/members`),
  addToOrganization: (userId, organizationId) =>
    api.post(`/admin/user-management/users/${userId}/organizations`, { organizationId }),
  removeFromOrganization: (userId, organizationId) =>
    api.delete(`/admin/user-management/users/${userId}/organizations/${organizationId}`),
  addToGroup: (userId, groupId) =>
    api.post(`/admin/user-management/users/${userId}/groups`, { groupId }),
  removeFromGroup: (userId, groupId) =>
    api.delete(`/admin/user-management/users/${userId}/groups/${groupId}`),
  grantRole: (userId, data) =>
    api.post(`/admin/user-management/users/${userId}/roles`, data),
  revokeRole: (userId, roleId) =>
    api.delete(`/admin/user-management/users/${userId}/roles/${roleId}`),
  getAbuseAlerts: (params = {}) =>
    api.get('/admin/user-management/abuse-alerts', { params }),
  updateAbuseAlert: (alertId, data) =>
    api.patch(`/admin/user-management/abuse-alerts/${alertId}`, data),
};

export const foldAccessAPI = {
  me: () => api.get('/admin/auth/me'),
  list: () => api.get('/admin/auth/access'),
  grant: (email, role) => api.post('/admin/auth/access', { email, role }),
  update: (accessId, updates) => api.patch(`/admin/auth/access/${accessId}`, updates),
};

export const runtimeLimitsAPI = {
  list: () => api.get('/admin/dashboard/runtime-limits'),
  usage: () => api.get('/admin/dashboard/runtime-limits/usage'),
  update: (key, body) =>
    api.patch(`/admin/dashboard/runtime-limits/${encodeURIComponent(key)}`, body),
};

export const subscriptionsAPI = {
  getTrials: () => api.get('/admin/subscriptions/trials'),
};

export const memberReportsAPI = {
  list: (params) => api.get('/admin/member-reports', { params }),
  get: (id) => api.get(`/admin/member-reports/${encodeURIComponent(id)}`),
  startReview: (id) => api.post(`/admin/member-reports/${encodeURIComponent(id)}/review`),
  resolve: (id, body) => api.post(`/admin/member-reports/${encodeURIComponent(id)}/resolve`, body),
};

export const onboardingFlowAPI = {
  getFlow: (params) => api.get('/admin/dashboard/analytics/onboarding/flow', { params }),
  getStepFlows: (step, params) =>
    api.get(`/admin/dashboard/analytics/onboarding/flow/steps/${encodeURIComponent(step)}/flows`, { params }),
  getFlowTimeline: (flowId) =>
    api.get(`/admin/dashboard/analytics/onboarding/flow/flows/${encodeURIComponent(flowId)}`),
};

export const flockBotSpendAPI = {
  get: (month) => api.get('/admin/dashboard/flockbot-spend', { params: month ? { month } : {} }),
};

export default api;
